import { Add, Delete } from '@mui/icons-material';
import { Box } from '@mui/material';
import React, { RefObject } from 'react';
import { TrusteeIndividual } from '../../models/InvestmentEntity';
import { StyledButton, StyledIconButton } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import { StyledTypography } from '../styles/styles';

interface TrusteeIndividualsSectionProps {
  trusteeIndividuals: TrusteeIndividual[];
  trusteeIndividualsError?: string;
  allErrors: Record<string, string | undefined>;
  trusteeFirstNameRef: RefObject<HTMLInputElement>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof TrusteeIndividual, value: string) => void;
}

export const TrusteeIndividualsSection: React.FC<TrusteeIndividualsSectionProps> = ({
  trusteeIndividuals,
  trusteeIndividualsError,
  allErrors,
  trusteeFirstNameRef,
  onAdd,
  onRemove,
  onUpdate,
}) => (
  <Box sx={{ mt: 2 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <StyledTypography variant="h6" sx={{ fontWeight: 600 }}>
        Trustee Individuals
      </StyledTypography>
      <StyledButton
        variant="outlined"
        size="small"
        onClick={onAdd}
        disabled={trusteeIndividuals.length >= 4}
        startIcon={<Add />}
      >
        Add Individual
      </StyledButton>
    </Box>

    {trusteeIndividualsError && (
      <StyledTypography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
        {trusteeIndividualsError}
      </StyledTypography>
    )}

    {trusteeIndividuals.map((individual, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <StyledTypography sx={{ width: '1.5rem' }}>{index + 1}.</StyledTypography>
        <StyledInput
          label="First Name"
          ref={index === trusteeIndividuals.length - 1 ? trusteeFirstNameRef : undefined}
          value={individual.firstName}
          onChange={(e) => onUpdate(index, 'firstName', e.target.value)}
          error={!!allErrors[`trusteeIndividuals[${index}].firstName`]}
          helperText={allErrors[`trusteeIndividuals[${index}].firstName`]}
          required
          fullWidth
        />
        <StyledInput
          label="Last Name"
          value={individual.lastName}
          onChange={(e) => onUpdate(index, 'lastName', e.target.value)}
          error={!!allErrors[`trusteeIndividuals[${index}].lastName`]}
          helperText={allErrors[`trusteeIndividuals[${index}].lastName`]}
          required
          fullWidth
        />
        <StyledIconButton onClick={() => onRemove(index)} size="small">
          <Delete />
        </StyledIconButton>
      </Box>
    ))}

    {trusteeIndividuals.length === 0 && (
      <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
        <StyledTypography variant="body2">
          Click "Add Individual" to add trustee individuals
        </StyledTypography>
      </Box>
    )}
  </Box>
);
